import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClosingDateHeadEmployee,
  ClosingDatePayload,
  ClosingDateReferences,
  ClosingDateRow,
  deleteClosingDate,
  getClosingDateReferences,
  getClosingDates,
  updateClosingDate,
  createClosingDate,
} from '../services/api';
import { SearchableSelect } from '../components/SearchableSelect';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { useAuth } from '../context/AuthContext';
import { SortState, compareValues, formatDate, toggleSort } from '../utils/table';
import '../styles/DataPage.css';
import './TaxesPage.css';
import './ClosingManagementPage.css';

type SortKey =
  | 'id'
  | 'entity'
  | 'task_code'
  | 'task_short_name'
  | 'contract_number'
  | 'contract_stage_name'
  | 'closing_stage_name'
  | 'closing_date'
  | 'note'
  | 'closed_by_name'
  | 'closed_by_user_name';

type FilterMenuKey =
  | 'id'
  | 'entity'
  | 'task_code'
  | 'task_short_name'
  | 'contract_number'
  | 'contract_stage_name'
  | 'closing_stage_name'
  | 'note'
  | 'closed_by_name'
  | 'closed_by_user_name'
  | null;

interface FormState {
  task_id: string;
  contract_id: string;
  contract_stage_id: string;
  closing_stage_id: string;
  closing_date: string;
  note: string;
  closed_by: string;
  closed_by_user: string;
}

const EMPTY_FORM: FormState = {
  task_id: '',
  contract_id: '',
  contract_stage_id: '',
  closing_stage_id: '',
  closing_date: '',
  note: '',
  closed_by: '',
  closed_by_user: '',
};

const ACCOUNTING_CLOSING_STAGE_ID = 9;

function dateInRange(value: string | undefined | null, from: string, to: string): boolean {
  if (!value?.trim()) {
    return !from.trim() && !to.trim();
  }
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) {
    return true;
  }
  const ts = new Date(value.slice(0, 10)).getTime();
  if (Number.isNaN(ts)) {
    return true;
  }
  const fromTs = hasFrom ? new Date(`${from}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? new Date(`${to}T23:59:59`).getTime() : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

function displayText(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function todayDateInputValue(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function headsForDepartment(
  refs: ClosingDateReferences | null,
  departmentId: string,
): ClosingDateHeadEmployee[] {
  if (!refs || !departmentId) {
    return [];
  }
  const group = refs.department_heads.find((item) => String(item.department_id) === departmentId);
  return group?.employees ?? [];
}

function resolveClosedByUser(heads: ClosingDateHeadEmployee[], current: string): string {
  if (heads.length === 1) {
    return String(heads[0].id);
  }
  if (current && heads.some((head) => String(head.id) === current)) {
    return current;
  }
  return '';
}

function employeeOptionsForDepartment(
  refs: ClosingDateReferences | null,
  departmentId: string,
  extra: ClosingDateHeadEmployee | null,
): ClosingDateHeadEmployee[] {
  const heads = headsForDepartment(refs, departmentId);
  if (!extra || !departmentId) {
    return heads;
  }
  if (heads.some((head) => head.id === extra.id)) {
    return heads;
  }
  return [...heads, extra];
}

const ClosingManagementPage: React.FC = () => {
  const { can, user } = useAuth();
  const canOpenPage = can('TASKS_CLOSING', 'EDIT');

  const [rows, setRows] = useState<ClosingDateRow[]>([]);
  const [references, setReferences] = useState<ClosingDateReferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('edit');
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<FilterMenuKey>(null);
  const [filterIds, setFilterIds] = useState<string[]>([]);
  const [filterEntities, setFilterEntities] = useState<string[]>([]);
  const [filterTaskCodes, setFilterTaskCodes] = useState<string[]>([]);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterContracts, setFilterContracts] = useState<string[]>([]);
  const [filterContractStages, setFilterContractStages] = useState<string[]>([]);
  const [filterStages, setFilterStages] = useState<string[]>([]);
  const [filterNotes, setFilterNotes] = useState<string[]>([]);
  const [filterDepartments, setFilterDepartments] = useState<string[]>([]);
  const [filterUsers, setFilterUsers] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [sortState, setSortState] = useState<SortState<SortKey>>({
    key: 'id',
    direction: 'desc',
  });

  const load = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setLoading(false);
      setRows([]);
      setReferences(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [data, refs] = await Promise.all([getClosingDates(), getClosingDateReferences()]);
      setRows(data);
      setReferences(refs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const selectedRow = useMemo(
    () => (selectedId == null ? null : rows.find((row) => row.id === selectedId) ?? null),
    [rows, selectedId],
  );

  useEffect(() => {
    if (selectedId != null && !rows.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [rows, selectedId]);

  const uniqueSorted = (values: string[]): string[] =>
    [...new Set(values)].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));

  const idFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => String(row.id))), [rows]);
  const entityFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => row.entity)), [rows]);
  const taskCodeFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => row.task_code)), [rows]);
  const nameFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => row.task_short_name)), [rows]);
  const contractFilterOptions = useMemo(
    () => uniqueSorted(rows.map((row) => row.contract_number || '—')),
    [rows],
  );
  const contractStageFilterOptions = useMemo(
    () => uniqueSorted(rows.map((row) => row.contract_stage_name || '—')),
    [rows],
  );
  const stageFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => row.closing_stage_name)), [rows]);
  const noteFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => displayText(row.note))), [rows]);
  const departmentFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => row.closed_by_name)), [rows]);
  const userFilterOptions = useMemo(() => uniqueSorted(rows.map((row) => row.closed_by_user_name)), [rows]);

  const clearFilters = (): void => {
    setFilterIds([]);
    setFilterEntities([]);
    setFilterTaskCodes([]);
    setFilterNames([]);
    setFilterContracts([]);
    setFilterContractStages([]);
    setFilterStages([]);
    setFilterNotes([]);
    setFilterDepartments([]);
    setFilterUsers([]);
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterIds.length > 0 ||
    filterEntities.length > 0 ||
    filterTaskCodes.length > 0 ||
    filterNames.length > 0 ||
    filterContracts.length > 0 ||
    filterContractStages.length > 0 ||
    filterStages.length > 0 ||
    filterNotes.length > 0 ||
    filterDepartments.length > 0 ||
    filterUsers.length > 0 ||
    filterDateFrom.trim() !== '' ||
    filterDateTo.trim() !== '';

  const filteredRows = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    return rows.filter((row) => {
      const searchable = [
        String(row.id),
        row.entity,
        row.task_code,
        row.task_short_name,
        row.contract_number,
        row.contract_stage_name,
        row.closing_stage_name,
        row.closing_date ? formatDate(row.closing_date) : '',
        row.note,
        row.closed_by_name,
        row.closed_by_user_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !text || searchable.includes(text);
      const matchesId = filterIds.length === 0 || filterIds.includes(String(row.id));
      const matchesEntity = filterEntities.length === 0 || filterEntities.includes(row.entity);
      const matchesTask = filterTaskCodes.length === 0 || filterTaskCodes.includes(row.task_code);
      const matchesName = filterNames.length === 0 || filterNames.includes(row.task_short_name);
      const matchesContract =
        filterContracts.length === 0 || filterContracts.includes(row.contract_number || '—');
      const matchesContractStage =
        filterContractStages.length === 0
        || filterContractStages.includes(row.contract_stage_name || '—');
      const matchesStage = filterStages.length === 0 || filterStages.includes(row.closing_stage_name);
      const matchesNote = filterNotes.length === 0 || filterNotes.includes(displayText(row.note));
      const matchesDept = filterDepartments.length === 0 || filterDepartments.includes(row.closed_by_name);
      const matchesUser = filterUsers.length === 0 || filterUsers.includes(row.closed_by_user_name);
      const matchesDate = dateInRange(row.closing_date, filterDateFrom, filterDateTo);
      return (
        matchesSearch &&
        matchesId &&
        matchesEntity &&
        matchesTask &&
        matchesName &&
        matchesContract &&
        matchesContractStage &&
        matchesStage &&
        matchesNote &&
        matchesDept &&
        matchesUser &&
        matchesDate
      );
    });
  }, [
    rows,
    searchText,
    filterIds,
    filterEntities,
    filterTaskCodes,
    filterNames,
    filterContracts,
    filterContractStages,
    filterStages,
    filterNotes,
    filterDepartments,
    filterUsers,
    filterDateFrom,
    filterDateTo,
  ]);

  const sortedRows = useMemo(() => {
    const values: Record<SortKey, (row: ClosingDateRow) => string | number | undefined> = {
      id: (row) => row.id,
      entity: (row) => row.entity,
      task_code: (row) => row.task_code,
      task_short_name: (row) => row.task_short_name,
      contract_number: (row) => row.contract_number ?? '',
      contract_stage_name: (row) => row.contract_stage_name ?? '',
      closing_stage_name: (row) => row.closing_stage_name,
      closing_date: (row) => row.closing_date,
      note: (row) => row.note ?? '',
      closed_by_name: (row) => row.closed_by_name,
      closed_by_user_name: (row) => row.closed_by_user_name,
    };
    const { key, direction } = sortState;
    return [...filteredRows].sort((left, right) =>
      compareValues(values[key](left), values[key](right), direction),
    );
  }, [filteredRows, sortState]);

  const handleSort = (key: SortKey): void => {
    setSortState((current) => toggleSort(current, key, key === 'id' || key === 'closing_date' ? 'desc' : 'asc'));
  };

  const openCreateModal = (): void => {
    const allComp = (references?.departments ?? []).find(
      (dept) => dept.code.trim().toUpperCase() === 'ALL_COMP',
    );
    const closedBy = allComp != null ? String(allComp.id) : '';
    const closedByUser = user?.id != null ? String(user.id) : '';
    setFormData({
      ...EMPTY_FORM,
      closing_date: todayDateInputValue(),
      closed_by: closedBy,
      closed_by_user: closedByUser,
    });
    setFormError('');
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = (): void => {
    if (!selectedRow) return;
    const closedBy = selectedRow.closed_by != null ? String(selectedRow.closed_by) : '';
    const heads = headsForDepartment(references, closedBy);
    const currentUser = selectedRow.closed_by_user != null ? String(selectedRow.closed_by_user) : '';
    const taskId = selectedRow.task_id != null ? String(selectedRow.task_id) : '';
    const contractStageId = selectedRow.contract_stage_id != null ? String(selectedRow.contract_stage_id) : '';
    setFormData({
      task_id: taskId,
      contract_id: taskId || contractStageId
        ? ''
        : selectedRow.contract_id != null ? String(selectedRow.contract_id) : '',
      contract_stage_id: contractStageId,
      closing_stage_id: String(selectedRow.closing_stage_id),
      closing_date: selectedRow.closing_date.slice(0, 10),
      note: selectedRow.note ?? '',
      closed_by: closedBy,
      closed_by_user: resolveClosedByUser(heads, currentUser),
    });
    setFormError('');
    setModalMode('edit');
    setModalOpen(true);
  };

  const closeModal = (): void => {
    if (saving) return;
    setModalOpen(false);
    setFormError('');
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (modalMode === 'edit' && !selectedRow) return;
    const taskId = formData.task_id ? Number(formData.task_id) : null;
    const contractId = formData.contract_id ? Number(formData.contract_id) : null;
    const contractStageId = formData.contract_stage_id ? Number(formData.contract_stage_id) : null;
    const stageId = Number(formData.closing_stage_id);
    const hasTask = taskId != null && Number.isInteger(taskId) && taskId > 0;
    const hasContract = contractId != null && Number.isInteger(contractId) && contractId > 0;
    const hasContractStage = contractStageId != null && Number.isInteger(contractStageId) && contractStageId > 0;
    const targetCount = Number(hasTask) + Number(hasContract) + Number(hasContractStage);
    if (targetCount > 1) {
      setFormError('Укажите либо задачу, либо этап договора, либо договор.');
      return;
    }
    if (targetCount === 0) {
      setFormError('Выберите задачу, этап договора или договор.');
      return;
    }
    if (!Number.isInteger(stageId) || stageId <= 0) {
      setFormError('Выберите тип.');
      return;
    }
    if (stageId === ACCOUNTING_CLOSING_STAGE_ID && !hasContract && !hasContractStage) {
      setFormError('Для закрытия бухгалтерией укажите договор.');
      return;
    }
    if (!formData.closing_date.trim()) {
      setFormError('Укажите дату закрытия.');
      return;
    }
    const payload: ClosingDatePayload = {
      task_id: hasTask ? taskId : null,
      contract_id: hasContract ? contractId : null,
      contract_stage_id: hasContractStage ? contractStageId : null,
      closing_stage_id: stageId,
      closing_date: formData.closing_date,
      note: formData.note.trim() || null,
      closed_by: formData.closed_by ? Number(formData.closed_by) : null,
      closed_by_user: formData.closed_by_user ? Number(formData.closed_by_user) : null,
    };
    setSaving(true);
    setFormError('');
    setError('');
    try {
      if (modalMode === 'create') {
        const created = await createClosingDate(payload);
        setRows((prev) => [created, ...prev]);
        setSelectedId(created.id);
      } else if (selectedRow) {
        const updated = await updateClosingDate(selectedRow.id, payload);
        setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      }
      setModalOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!selectedRow || isDeleting) {
      return;
    }
    const confirmed = window.confirm(
      `Удалить запись закрытия №${selectedRow.id} (задача ${selectedRow.task_code})?`,
    );
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      await deleteClosingDate(selectedRow.id);
      setRows((prev) => prev.filter((row) => row.id !== selectedRow.id));
      setSelectedId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка удаления');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDepartmentChange = (departmentId: string): void => {
    const heads = headsForDepartment(references, departmentId);
    setFormData((prev) => ({
      ...prev,
      closed_by: departmentId,
      closed_by_user: resolveClosedByUser(heads, prev.closed_by_user),
    }));
  };

  const taskOptions = useMemo(
    () =>
      (references?.tasks ?? []).map((task) => ({
        value: String(task.id),
        label: `${task.task_id} — ${task.short_name}`,
      })),
    [references],
  );

  const contractOptions = useMemo(
    () =>
      (references?.contracts ?? []).map((contract) => ({
        value: String(contract.id),
        label: contract.contract_number,
      })),
    [references],
  );

  const handleTaskChange = (value: string): void => {
    setFormData((prev) => ({
      ...prev,
      task_id: value,
      contract_id: value ? '' : prev.contract_id,
      contract_stage_id: value ? '' : prev.contract_stage_id,
    }));
  };

  const handleContractChange = (value: string): void => {
    setFormData((prev) => ({
      ...prev,
      contract_id: value,
      task_id: value ? '' : prev.task_id,
      contract_stage_id: value ? '' : prev.contract_stage_id,
    }));
  };

  const handleContractStageChange = (value: string): void => {
    setFormData((prev) => ({
      ...prev,
      contract_stage_id: value,
      task_id: value ? '' : prev.task_id,
      contract_id: value ? '' : prev.contract_id,
    }));
  };

  const contractStageOptions = useMemo(
    () =>
      (references?.contract_stages ?? []).map((stage) => ({
        value: String(stage.id),
        label: `${stage.contract_number} — ${stage.stage_number}. ${stage.name}`,
      })),
    [references],
  );

  const taskFieldLocked = Boolean(formData.contract_id || formData.contract_stage_id);
  const contractFieldLocked = Boolean(formData.task_id || formData.contract_stage_id);
  const contractStageFieldLocked = Boolean(formData.task_id || formData.contract_id);

  const headEmployeeOptions = useMemo(
    () =>
      employeeOptionsForDepartment(
        references,
        formData.closed_by,
        user ? { id: user.id, full_name: user.full_name } : null,
      ),
    [references, formData.closed_by, user],
  );

  const colgroup = (
    <colgroup>
      <col className="closing-management-page__colgrid closing-management-page__colgrid--id" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--entity" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--task-id" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--name" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--contract" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--contract-stage" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--stage" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--date" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--note" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--person" />
      <col className="closing-management-page__colgrid closing-management-page__colgrid--person" />
    </colgroup>
  );

  return (
    <div className="data-page articles-page taxes-page closing-management-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице (требуется TASKS_CLOSING EDIT).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Даты закрытия</h1>
            <div className="page-header-row__actions">
              <button type="button" className="create-button" onClick={openCreateModal}>
                Создать
              </button>
              {selectedRow ? (
                <>
                  <button type="button" className="secondary-button" onClick={openEditModal}>
                    Редактировать
                  </button>
                  <button
                    type="button"
                    className="delete-row-button"
                    onClick={() => void handleDelete()}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Удаление…' : 'Удалить'}
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen ? (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="ID"
                      options={idFilterOptions}
                      selected={filterIds}
                      onSelectedChange={setFilterIds}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'id'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'id' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Сущность"
                      options={entityFilterOptions}
                      selected={filterEntities}
                      onSelectedChange={setFilterEntities}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'entity'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'entity' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="ID задачи"
                      options={taskCodeFilterOptions}
                      selected={filterTaskCodes}
                      onSelectedChange={setFilterTaskCodes}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'task_code'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'task_code' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Название задачи"
                      options={nameFilterOptions}
                      selected={filterNames}
                      onSelectedChange={setFilterNames}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'task_short_name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'task_short_name' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Договор"
                      options={contractFilterOptions}
                      selected={filterContracts}
                      onSelectedChange={setFilterContracts}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contract_number'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contract_number' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Этап договора"
                      options={contractStageFilterOptions}
                      selected={filterContractStages}
                      onSelectedChange={setFilterContractStages}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contract_stage_name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contract_stage_name' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Тип"
                      options={stageFilterOptions}
                      selected={filterStages}
                      onSelectedChange={setFilterStages}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'closing_stage_name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'closing_stage_name' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Дата закрытия</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        title="С"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        title="По"
                      />
                    </div>
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Примечание"
                      options={noteFilterOptions}
                      selected={filterNotes}
                      onSelectedChange={setFilterNotes}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'note'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'note' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Отдел"
                      options={departmentFilterOptions}
                      selected={filterDepartments}
                      onSelectedChange={setFilterDepartments}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'closed_by_name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'closed_by_name' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Сотрудник"
                      options={userFilterOptions}
                      selected={filterUsers}
                      onSelectedChange={setFilterUsers}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'closed_by_user_name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'closed_by_user_name' : null)}
                    />
                  </div>
                </div>
              </aside>
            ) : null}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table taxes-page__table closing-management-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          <th className="sortable" onClick={() => handleSort('id')} role="button">
                            ID
                          </th>
                          <th className="sortable" onClick={() => handleSort('entity')} role="button">
                            Сущность
                          </th>
                          <th className="sortable" onClick={() => handleSort('task_code')} role="button">
                            ID задачи
                          </th>
                          <th className="sortable" onClick={() => handleSort('task_short_name')} role="button">
                            Название задачи
                          </th>
                          <th className="sortable" onClick={() => handleSort('contract_number')} role="button">
                            Договор
                          </th>
                          <th className="sortable" onClick={() => handleSort('contract_stage_name')} role="button">
                            Этап договора
                          </th>
                          <th className="sortable" onClick={() => handleSort('closing_stage_name')} role="button">
                            Тип
                          </th>
                          <th className="sortable closing-management-page__th-date" onClick={() => handleSort('closing_date')} role="button">
                            Дата закрытия
                          </th>
                          <th className="sortable" onClick={() => handleSort('note')} role="button">
                            Примечание
                          </th>
                          <th className="sortable" onClick={() => handleSort('closed_by_name')} role="button">
                            Отдел
                          </th>
                          <th className="sortable" onClick={() => handleSort('closed_by_user_name')} role="button">
                            Сотрудник
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.length === 0 ? (
                          <tr>
                            <td colSpan={11} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedRows.map((row) => (
                            <tr
                              key={row.id}
                              className={`clickable-row${selectedId === row.id ? ' data-table__row--selected' : ''}`}
                              onClick={() => setSelectedId(row.id)}
                            >
                              <td>{row.id}</td>
                              <td>{row.entity}</td>
                              <td>{row.task_code}</td>
                              <td>
                                <div className="contracts-page__clamp-2">{displayText(row.task_short_name)}</div>
                              </td>
                              <td>{displayText(row.contract_number)}</td>
                              <td>{displayText(row.contract_stage_name)}</td>
                              <td>{row.closing_stage_name}</td>
                              <td className="closing-management-page__td-date">
                                {row.closing_date ? formatDate(row.closing_date) : '—'}
                              </td>
                              <td>
                                <div className="contracts-page__clamp-2">{displayText(row.note)}</div>
                              </td>
                              <td>{row.closed_by_name}</td>
                              <td>{row.closed_by_user_name}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer taxes-page__table closing-management-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedRows.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>

          {modalOpen ? (
            <div className="taxes-page__modal-overlay" role="presentation" onClick={closeModal}>
              <div
                className="taxes-page__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="closing-date-modal-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="closing-date-modal-title">
                  {modalMode === 'create' ? 'Новая запись закрытия' : 'Редактирование закрытия'}
                </h2>
                <form className="taxes-page__form" onSubmit={(e) => void handleSubmit(e)}>
                  <label
                    className="taxes-page__field"
                    title={taskFieldLocked ? 'Сначала очистите договор или этап договора' : undefined}
                  >
                    <span>Задача</span>
                    <SearchableSelect
                      options={taskOptions}
                      value={formData.task_id}
                      onChange={handleTaskChange}
                      disabled={saving || taskFieldLocked}
                      allowEmpty
                      emptyLabel="—"
                      placeholder="Поиск задачи…"
                      ariaLabel="Задача"
                    />
                  </label>
                  <label
                    className="taxes-page__field"
                    title={contractStageFieldLocked ? 'Сначала очистите задачу или договор' : undefined}
                  >
                    <span>Этап договора</span>
                    <SearchableSelect
                      options={contractStageOptions}
                      value={formData.contract_stage_id}
                      onChange={handleContractStageChange}
                      disabled={saving || contractStageFieldLocked}
                      allowEmpty
                      emptyLabel="—"
                      placeholder="Поиск этапа договора…"
                      ariaLabel="Этап договора"
                    />
                  </label>
                  <label
                    className="taxes-page__field"
                    title={contractFieldLocked ? 'Сначала очистите задачу или этап договора' : undefined}
                  >
                    <span>Договор</span>
                    <SearchableSelect
                      options={contractOptions}
                      value={formData.contract_id}
                      onChange={handleContractChange}
                      disabled={saving || contractFieldLocked}
                      allowEmpty
                      emptyLabel="—"
                      placeholder="Поиск договора…"
                      ariaLabel="Договор"
                    />
                  </label>
                  <label className="taxes-page__field">
                    <span>Тип *</span>
                    <select
                      value={formData.closing_stage_id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, closing_stage_id: e.target.value }))}
                      disabled={saving}
                      required
                    >
                      <option value="">—</option>
                      {(references?.stages ?? []).map((stage) => (
                        <option key={stage.stage_id} value={stage.stage_id}>
                          {stage.stage_name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="taxes-page__field">
                    <span>Дата закрытия *</span>
                    <input
                      type="date"
                      value={formData.closing_date}
                      onChange={(e) => setFormData((prev) => ({ ...prev, closing_date: e.target.value }))}
                      disabled={saving}
                      required
                    />
                  </label>
                  <label className="taxes-page__field">
                    <span>Примечание</span>
                    <input
                      type="text"
                      maxLength={100}
                      value={formData.note}
                      onChange={(e) => setFormData((prev) => ({ ...prev, note: e.target.value }))}
                      disabled={saving}
                    />
                  </label>
                  <label className="taxes-page__field">
                    <span>Отдел</span>
                    <select
                      value={formData.closed_by}
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">—</option>
                      {(references?.departments ?? []).map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="taxes-page__field">
                    <span>Сотрудник</span>
                    <select
                      value={formData.closed_by_user}
                      onChange={(e) => setFormData((prev) => ({ ...prev, closed_by_user: e.target.value }))}
                      disabled={saving || !formData.closed_by}
                    >
                      <option value="">—</option>
                      {headEmployeeOptions.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.full_name}
                        </option>
                      ))}
                    </select>
                  </label>

                  {formError ? <div className="error-message">{formError}</div> : null}

                  <div className="taxes-page__modal-actions">
                    <button type="button" className="secondary-button" onClick={closeModal} disabled={saving}>
                      Отмена
                    </button>
                    <button type="submit" className="create-button" disabled={saving}>
                      {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ClosingManagementPage;
